/**
 * Napkin physics & packaging conversions (brief §4.1, design developer notes).
 * All constants come from company settings / item attributes — never hard-coded
 * in callers. Defaults below mirror the confirmed design constants for previews.
 */
import { D, roundTo, type Dec, type DecimalInput } from './money';

export interface PackagingSpec {
  /** pieces per pack (paket içi adet) */
  packQty: number;
  /** packs per case (koli içi paket) */
  casePacks: number;
  /** napkin weight in grams */
  napkinWeightG: DecimalInput;
  /** net bobbin weight in kg */
  bobbinNetKg?: DecimalInput;
  /** bobbins per pallet */
  palletBobbins?: number;
}

/** Confirmed by Mert (21 Sep 2026) — see design/developer-notes.md. Used as wizard defaults only. */
export const DESIGN_DEFAULT_PACKAGING: PackagingSpec = {
  packQty: 50,
  casePacks: 16,
  napkinWeightG: '8.33',
  bobbinNetKg: '83.3',
  palletBobbins: 6,
};

/** Material weight of one open napkin: (w × h / 10 000) × gsm → grams. 40×40 @ 55 = 8,8 g */
export function napkinWeightG(openWidthCm: DecimalInput, openHeightCm: DecimalInput, gsm: DecimalInput): Dec {
  return D(openWidthCm).times(D(openHeightCm)).dividedBy(10_000).times(D(gsm));
}

/** Pieces obtainable from a bobbin: kg × 1000 / g × (1 − scrap%) — floored. */
export function bobbinYield(bobbinNetKg: DecimalInput, napkinG: DecimalInput, scrapPct: DecimalInput = 0): Dec {
  const g = D(napkinG);
  if (g.lessThanOrEqualTo(0)) throw new RangeError('Peçete ağırlığı sıfırdan büyük olmalı');
  return D(bobbinNetKg).times(1000).dividedBy(g).times(D(1).minus(D(scrapPct).dividedBy(100))).floor();
}

export type PackUnit = 'pcs' | 'pack' | 'case' | 'kg' | 'roll' | 'pallet';

export function piecesPerUnit(unit: PackUnit, spec: PackagingSpec): Dec {
  switch (unit) {
    case 'pcs':
      return D(1);
    case 'pack':
      return D(spec.packQty);
    case 'case':
      return D(spec.packQty).times(spec.casePacks);
    case 'kg':
      return D(1000).dividedBy(D(spec.napkinWeightG));
    case 'roll':
      if (!spec.bobbinNetKg) throw new RangeError('Bobin ağırlığı tanımlı değil');
      return D(spec.bobbinNetKg).times(1000).dividedBy(D(spec.napkinWeightG));
    case 'pallet':
      if (!spec.bobbinNetKg || !spec.palletBobbins) throw new RangeError('Palet tanımı eksik');
      return D(spec.bobbinNetKg).times(1000).dividedBy(D(spec.napkinWeightG)).times(spec.palletBobbins);
  }
}

export function convertQty(qty: DecimalInput, from: PackUnit, to: PackUnit, spec: PackagingSpec): Dec {
  return D(qty).times(piecesPerUnit(from, spec)).dividedBy(piecesPerUnit(to, spec));
}

/** Kilograms of material for a number of pieces. */
export function piecesToKg(pieces: DecimalInput, spec: PackagingSpec): Dec {
  return D(pieces).times(D(spec.napkinWeightG)).dividedBy(1000);
}

/** Round a quantity up to a multiple (e.g. MRP → whole cases, bobbins → full pallets). */
export function roundUpToMultiple(qty: DecimalInput, multiple: DecimalInput): Dec {
  const m = D(multiple);
  if (m.lessThanOrEqualTo(0)) return D(qty);
  return D(qty).dividedBy(m).ceil().times(m);
}

export interface PackagingSummary {
  piecesPerCase: Dec;
  caseKg: Dec;
  bobbinPieces?: Dec;
  bobbinCases?: Dec;
  palletKg?: Dec;
  palletCases?: Dec;
}

/** "1 koli = 16 paket = 800 adet ≈ 6,66 kg · 1 bobin = 83,3 kg = 12,5 koli …" */
export function packagingSummary(spec: PackagingSpec): PackagingSummary {
  const piecesPerCase = piecesPerUnit('case', spec);
  const res: PackagingSummary = {
    piecesPerCase,
    caseKg: roundTo(piecesToKg(piecesPerCase, spec), 3),
  };
  if (spec.bobbinNetKg) {
    res.bobbinPieces = D(spec.bobbinNetKg).times(1000).dividedBy(D(spec.napkinWeightG)).toDecimalPlaces(0);
    res.bobbinCases = roundTo(res.bobbinPieces.dividedBy(piecesPerCase), 2);
    if (spec.palletBobbins) {
      res.palletKg = D(spec.bobbinNetKg).times(spec.palletBobbins);
      res.palletCases = roundTo(res.bobbinCases.times(spec.palletBobbins), 1);
    }
  }
  return res;
}
